import React, { useState, useEffect } from 'react';
import { purchasesVendorsApi, purchasesBillsApi, type VendorDto } from '../../services/api';

export type BillLineRow = {
  id: string;
  description: string;
  quantity: number;
  price: number;
  tax_percentage: number;
};

function formatPeso(amount: number): string {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function lineTotal(qty: number, price: number, taxPct: number): number {
  return Math.round(qty * price * (1 + taxPct / 100) * 100) / 100;
}

interface RecordBillModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function RecordBillModal({ open, onClose, onSuccess }: RecordBillModalProps) {
  const [vendors, setVendors] = useState<VendorDto[]>([]);
  const [vendorId, setVendorId] = useState('');
  const [billDate, setBillDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [vendorInvoiceRef, setVendorInvoiceRef] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<BillLineRow[]>([
    { id: '1', description: '', quantity: 1, price: 0, tax_percentage: 0 },
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      purchasesVendorsApi.list().then((res) => {
        if (res.data) setVendors(res.data);
      });
    }
  }, [open]);

  const updateLine = (id: string, field: keyof BillLineRow, value: string | number) => {
    setLines((prev) =>
      prev.map((l) => (l.id === id ? { ...l, [field]: value } : l))
    );
  };

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      {
        id: String(Date.now()),
        description: '',
        quantity: 1,
        price: 0,
        tax_percentage: 0,
      },
    ]);
  };

  const subtotal = lines.reduce((sum, l) => sum + l.quantity * l.price, 0);
  const taxTotal = lines.reduce(
    (sum, l) => sum + (l.quantity * l.price * l.tax_percentage) / 100,
    0
  );
  const total = Math.round((subtotal + taxTotal) * 100) / 100;

  const handleSave = async () => {
    if (!vendorId) {
      setError('Please select a vendor.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const res = await purchasesBillsApi.create({
        vendor_id: vendorId,
        bill_date: billDate,
        due_date: dueDate,
        vendor_invoice_ref: vendorInvoiceRef.trim() || undefined,
        notes: notes.trim() || undefined,
        line_items: lines.map((l) => ({
          description: l.description.trim() || 'Expense',
          quantity: l.quantity,
          price: l.price,
          tax_percentage: l.tax_percentage,
        })),
      });
      if (res.errors) throw new Error('Validation failed');
      onSuccess();
      setVendorId('');
      setBillDate(new Date().toISOString().slice(0, 10));
      const d = new Date();
      d.setDate(d.getDate() + 30);
      setDueDate(d.toISOString().slice(0, 10));
      setVendorInvoiceRef('');
      setNotes('');
      setLines([{ id: String(Date.now()), description: '', quantity: 1, price: 0, tax_percentage: 0 }]);
      onClose();
    } catch {
      setError('Failed to save bill. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col bg-white rounded-2xl shadow-xl border border-gray-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="record-bill-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 id="record-bill-title" className="text-xl font-bold text-gray-900">
            Record New Bill
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 text-red-700 px-4 py-2 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vendor <span className="text-red-500">*</span>
              </label>
              <select
                value={vendorId}
                onChange={(e) => setVendorId(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
              >
                <option value="">Select vendor</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.vendor_name}
                  </option>
                ))}
              </select>
            </div>
            <div />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bill Date</label>
              <input
                type="date"
                value={billDate}
                onChange={(e) => setBillDate(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vendor Invoice # / Reference
            </label>
            <input
              type="text"
              value={vendorInvoiceRef}
              onChange={(e) => setVendorInvoiceRef(e.target.value)}
              placeholder="Vendor's invoice number"
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
            />
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">Line items</div>
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left font-semibold text-gray-600 py-2 px-3">Description</th>
                    <th className="text-left font-semibold text-gray-600 py-2 px-3 w-20">Qty</th>
                    <th className="text-left font-semibold text-gray-600 py-2 px-3 w-28">Price</th>
                    <th className="text-left font-semibold text-gray-600 py-2 px-3 w-20">Tax %</th>
                    <th className="text-right font-semibold text-gray-600 py-2 px-3 w-28">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l.id} className="border-b border-gray-100 last:border-0">
                      <td className="py-2 px-3">
                        <input
                          type="text"
                          value={l.description}
                          onChange={(e) => updateLine(l.id, 'description', e.target.value)}
                          placeholder="Expense description"
                          className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-gray-900 placeholder-gray-400 focus:ring-1 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                        />
                      </td>
                      <td className="py-2 px-3">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={l.quantity}
                          onChange={(e) => updateLine(l.id, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-gray-900 focus:ring-1 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                        />
                      </td>
                      <td className="py-2 px-3">
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={l.price}
                          onChange={(e) => updateLine(l.id, 'price', parseFloat(e.target.value) || 0)}
                          className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-gray-900 focus:ring-1 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                        />
                      </td>
                      <td className="py-2 px-3">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={0.01}
                          value={l.tax_percentage}
                          onChange={(e) => updateLine(l.id, 'tax_percentage', parseFloat(e.target.value) || 0)}
                          className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-gray-900 focus:ring-1 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                        />
                      </td>
                      <td className="py-2 px-3 text-right text-gray-700 font-medium">
                        {formatPeso(lineTotal(l.quantity, l.price, l.tax_percentage))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              onClick={addLine}
              className="mt-2 text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              + Add Line
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-gray-100">
            <div>
              <div className="text-sm text-gray-500">Subtotal</div>
              <div className="text-lg font-medium text-gray-900">{formatPeso(subtotal)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Tax</div>
              <div className="text-lg font-medium text-gray-900">{formatPeso(taxTotal)}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Total</div>
              <div className="text-lg font-bold text-red-600">{formatPeso(total)}</div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes"
              rows={2}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none text-sm"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-60"
          >
            {saving ? 'Saving...' : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                Save Bill
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
